import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import LooxsLogo from "@/components/LooxsLogo";
import PageMeta from "@/components/PageMeta";

const FOUNDER_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_looxs-ecommerce/artifacts/p0auqnyq_30BA266F-4E88-4859-BD2C-64C6C4891FCF.jpeg";

export default function About() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/company").then(r => setC(r.data)); }, []);
  const about = c?.about || {};
  return (
    <div className="bg-ivory text-ink">
      <PageMeta title="About LOOXS · The House" description="LOOXS is a private universe of contemporary luxury structured across eleven chapters, designed in London and crafted in India. Learn the story of the house and its founder Mohammad Firoz." />
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "#efe7d7" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, #f5ecd8 0%, #ead9b6 55%, #d9c39a 100%)" }} />
        <div className="grain opacity-40" />
        <div className="relative max-w-[1200px] mx-auto px-8 md:px-16 pt-40 pb-24 text-center">
          <div className="mb-10 flex justify-center"><LooxsLogo height={90} /></div>
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">The House</p>
          <h1 className="font-editorial text-5xl md:text-8xl leading-[0.95] tracking-tight">About LOOXS</h1>
          <div className="rule-gold w-32 mx-auto my-10" />
          <p className="max-w-2xl mx-auto text-ink/75 leading-relaxed text-lg">{about.opening}</p>
        </div>
      </section>

      <Chapter label="How we started" body={about.how_started} imgSide="right" />
      <Chapter label="The Founder" title="Mohammad Firoz" body={"Founder & CEO. LOOXS is his vision — a house built on the idea that luxury should feel personal, quality should be uncompromised, and fashion should empower individuals to express their best selves every single day."} imgSide="left" img={FOUNDER_IMG} />

      {/* Founder letter */}
      <section className="py-24 md:py-32" style={{ background: "#f4ecd8" }}>
        <div className="max-w-3xl mx-auto text-center px-8">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">A note from the Founder</p>
          <div className="rule-gold w-16 mx-auto mb-10" />
          <p className="font-editorial-italic text-2xl md:text-4xl leading-relaxed text-ink/85">"{about.founder_message}"</p>
          <p className="mt-10 font-editorial-italic text-2xl text-goldDark">— Mohammad Firoz</p>
          <p className="text-[10px] tracking-widest-plus uppercase text-ink/50 mt-1">Founder, LOOXS</p>
          <p className="text-sm mt-6"><span className="text-[10px] tracking-widest-plus uppercase text-ink/50 mr-2">Office of the CEO ·</span><a href="mailto:ceo@looxsfashion.com" data-testid="about-ceo-email" className="link-draw text-goldDark">ceo@looxsfashion.com</a></p>
        </div>
      </section>

      <Chapter label="Our vision" body={about.vision} />
      <Chapter label="Our mission" body={about.mission} imgSide="right" />
      <Chapter label="Our philosophy" body={about.philosophy} />
      <Chapter label="London Wear" body={about.london_wear} imgSide="right" />

      {/* 11 Chapters */}
      <section className="py-24 md:py-32 bg-ivory">
        <div className="max-w-[1200px] mx-auto px-8 md:px-16 text-center">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">The Archive</p>
          <h2 className="font-editorial text-4xl md:text-6xl tracking-tight">The Eleven Chapters</h2>
          <div className="rule-gold w-24 mx-auto my-8" />
          <p className="max-w-2xl mx-auto text-ink/70 leading-relaxed mb-10">Eleven categories, one identity. Each chapter is a distinct study — from denim to eyewear — carrying the same LOOXS DNA.</p>
          <Link to="/chapters" className="tracking-widest-plus text-[11px] uppercase text-goldDark link-draw">Enter the chapters →</Link>
        </div>
      </section>

      <section className="py-24 md:py-32 text-center" style={{ background: "#e5dbc7" }}>
        <div className="max-w-3xl mx-auto px-8">
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-6">Crafted for the future</p>
          <p className="font-editorial text-3xl md:text-5xl leading-tight">A house designed to grow. A story only just beginning.</p>
        </div>
      </section>
    </div>
  );
}

function Chapter({ label, title, body, imgSide, img }) {
  if (!body) return null;
  const hasImg = !!img;
  return (
    <section className={`py-24 md:py-32 bg-ivory`}>
      <div className={`max-w-[1200px] mx-auto px-8 md:px-16 grid ${hasImg ? "md:grid-cols-2" : ""} gap-12 md:gap-16 items-center`}>
        {hasImg && imgSide === "left" && (<img src={img} alt="" className="w-full aspect-[4/5] object-cover" />)}
        <div>
          <p className="text-[10px] tracking-luxury uppercase text-goldDark mb-4">{label}</p>
          {title && <h2 className="font-editorial text-4xl md:text-6xl tracking-tight leading-[1.05]">{title}</h2>}
          {title && <div className="rule-gold w-16 my-6" />}
          <p className="text-lg leading-relaxed text-ink/80 font-light">{body}</p>
        </div>
        {hasImg && imgSide === "right" && (<img src={img} alt="" className="w-full aspect-[4/5] object-cover" />)}
      </div>
    </section>
  );
}
